import React, {
  useLayoutEffect,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import styles from "./MarketingPage.module.css";
import ecosystemImage from "./images/ecosystem.jpg";
import innovationImage from "./images/innovation.jpg";
import globalAccessImage from "./images/globalAccess.jpg";
import potentialImage from "./images/chess.png";
import championsImage from "./images/runner.png";
import dataImage from "./images/carrom.png";
import newsImage from "./images/news.jpg";
import companiesImage from "./images/companies.jpg";
import investorsImage from "./images/investors.jpg";
import technologistsImage from "./images/technologists.jpg";
import athletesImage from "./images/athletes.jpg";
import coachesImage from "./images/coaches.jpg";
import nutritionistsImage from "./images/nutritionists.jpg";
import socialMediaImage from "./images/socialmedia.jpg";
import playersImage from "./images/players.jpg";
import arenaImage from "./images/arena.jpg";
import profilingImage from "./images/profiling.jpg";
import newsModuleImage from "./images/newsModule.jpg";
import adsImage from "./images/ads.jpg";
import arenaIcon from "./images/icons/arena-icon.png";
import profilingIcon from "./images/icons/profiling-icon.png";
import newsIcon from "./images/icons/news-icon.webp";
import adsIcon from "./images/icons/ads-icon.png";
import personImage from "./images/person.png";
import personImage2 from "./images/person2.png";
import personImage3 from "./images/person3.png";
import HeaderMain from "./Components/HeaderMain.jsx";

gsap.registerPlugin(ScrollTrigger);

const MarketingPage = () => {
  const navigate = useNavigate();

  const contents = [
    {
      heading: "Changing the Way the World Experiences Sports",
      description:
        "Dsportshub unites the entire sports ecosystem—athletes, tech, media, investors, and fans—through AI and data-powered innovation. This is where passion meets intelligence, and the future of sports begins.",
      image: potentialImage,
    },
    {
      heading: "Speed, Access, and Intelligence at Your Fingertips",
      description:
        "From live updates to in-depth insights, Dsportshub delivers real-time, lightning-fast access to everything sports. Wherever you are, whatever you love—we’re your always-on sports companion.",
      image: championsImage,
    },
    {
      heading: "Built for Collaboration. Driven by Innovation.",
      description:
        "Our vibrant platform empowers athletes, creators, coaches, brands, and fans. With cutting-edge tech and a community-first mindset, Dsportshub is more than a platform—it's your winning edge.",
      image: dataImage,
    },
  ];

  const whyChooseUsFeatures = [
    {
      heading: "Innovative Technology Solutions",
      description:
        "Leverage cutting-edge technology to enhance your sports experience. From AI-driven insights to immersive content, we are at the forefront of sports innovation.",
      image: innovationImage,
    },
    {
      heading: "Connected Sports Ecosystem",
      description:
        "DSPORTSHUB unites athletes, coaches, technologists, investors, entrepreneurs, and fans in a single, data-powered platform. We are transforming how the world connects through sports.",
      image: ecosystemImage,
    },
    {
      heading: "Real-Time, Global Access",
      description:
        "Experience lightning-fast access to the latest news, stats, and insights from anywhere in the world. We are your go-to destination for everything sports, updated in real time.",
      image: globalAccessImage,
    },
  ];

  const testimonials = [
    {
      name: "Alex Thompson",
      role: "Professional Athlete",
      quote:
        "Dsportshub has transformed how I connect with fans and track my performance. It's a game-changer for athletes.",
      image: personImage,
    },
    {
      name: "John Doe",
      role: "Sports Brand Manager",
      quote:
        "The advertising platform on Dsportshub delivers unmatched precision and engagement. Our campaigns have never performed better.",
      image: personImage2,
    },
    {
      name: "Dr. James Carter",
      role: "Sports Nutritionist",
      quote:
        "The insights and community on Dsportshub help me provide tailored advice to athletes, elevating their performance.",
      image: personImage3,
    },
  ];

  const modules = [
    {
      title: "Arena",
      description:
        "Immerse yourself in the ultimate interactive sports hub. Compete, connect, and engage with live events and challenges in real-time.",
      image: arenaImage,
      icon: arenaIcon,
      cta: "View Arena",
    },
    {
      title: "Player Profiling",
      description:
        "Unlock deep insights with AI-driven player analytics. Track performance, strengths, and growth opportunities like never before.",
      image: profilingImage,
      icon: profilingIcon,
      cta: "Explore Profiles",
    },
    {
      title: "News",
      description:
        "Stay ahead with real-time sports news and updates. From scores to stories, get the latest from the world of sports instantly.",
      image: newsModuleImage,
      icon: newsIcon,
      cta: "Read News",
    },
    {
      title: "Advertisement Platform",
      description:
        "Reach your audience with precision. Our platform connects brands with sports fans through targeted, high-impact campaigns.",
      image: adsImage,
      icon: adsIcon,
      cta: "Advertise Now",
    },
  ];

  const heroRef = useRef(null);
  const whyChooseUsRef = useRef(null);
  const aboutRef = useRef(null);
  const modulesRef = useRef(null);
  const testimonialsRef = useRef(null);
  const ctaRef = useRef(null);
  const footerRef = useRef(null);
  const iconRefs = useRef([]);
  const nodeRefs = useRef([]);
  const moduleCardRefs = useRef([]);
  const dotRefs = useRef([]);
  const testimonialDotRefs = useRef([]);

  const isHeroInView = useInView(heroRef, { once: false });
  const isWhyChooseUsInView = useInView(whyChooseUsRef, {
    once: false,
    margin: "0px 0px -100px 0px",
  });
  const isAboutInView = useInView(aboutRef, {
    once: false,
    margin: "0px 0px -300px 0px",
  });
  const isModulesInView = useInView(modulesRef, {
    once: false,
    margin: "0px 0px -200px 0px",
  });
  const isTestimonialsInView = useInView(testimonialsRef, {
    once: false,
    margin: "0px 0px -200px 0px",
  });
  const isCtaInView = useInView(ctaRef, {
    once: false,
    margin: "0px 0px -200px 0px",
  });

  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentSlideChoose, setCurrentSlideChoose] = useState(0);
  const [currentTestimonialSlide, setCurrentTestimonialSlide] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [direction, setDirection] = useState(1);
  const [directionChoose, setDirectionChoose] = useState(1);
  const [testimonialDirection, setTestimonialDirection] = useState(1);
  const [isMobileView, setIsMobileView] = useState(0);

  const ecosystemRoles = [
    { image: newsImage, type: "landscape" },
    { image: companiesImage, type: "landscape" },
    { image: investorsImage, type: "portrait" },
    { image: technologistsImage, type: "portrait" },
    { image: athletesImage, type: "portrait" },
    { image: coachesImage, type: "landscape" },
    { image: nutritionistsImage, type: "landscape" },
    { image: socialMediaImage, type: "portrait" },
    { image: playersImage, type: "portrait" },
  ];

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const baseIcons = [
    { icon: "🏀", id: "basketball" },
    { icon: "⚽", id: "soccer" },
    { icon: "🎾", id: "tennis" },
    { icon: "🏈", id: "football" },
    { icon: "🏐", id: "volleyball" },
    { icon: "🏒", id: "hockey" },
    { icon: "⚾", id: "baseball" },
    { icon: "🏓", id: "pingpong" },
  ];

  const iconPositions = useMemo(() => {
    const generateIconPositions = (count = 10) => {
      const positions = [];
      for (let i = 0; i < count; i++) {
        const left = Math.round(Math.random() * 90 + 5);
        const top = Math.round(Math.random() * 90 + 5);
        const scale = parseFloat((Math.random() * 0.5 + 0.7).toFixed(2));
        let isFarEnough = true;
        for (const p of positions) {
          const dx = p.left - left;
          const dy = p.top - top;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 10) {
            isFarEnough = false;
            break;
          }
        }
        if (isFarEnough) {
          positions.push({ left, top, scale });
        } else {
          i--;
        }
      }
      return positions;
    };
    return generateIconPositions(10);
  }, []);

  const sportsIcons = useMemo(() => {
    return iconPositions.map((pos, index) => {
      const randomIcon =
        baseIcons[Math.floor(Math.random() * baseIcons.length)];
      return { ...randomIcon, key: `${randomIcon.id}-${index}`, ...pos };
    });
  }, [iconPositions]);

  const nodePositions = useMemo(() => {
    const isMobile = window.innerWidth <= 768;
    const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;
    const wrapperWidth = isMobile ? 300 : isTablet ? 350 : 400;
    const wrapperHeight = wrapperWidth;
    isMobile ? setIsMobileView(1) : setIsMobileView(0);
    const desktopPositions = [
      { x: 110, y: 180 },
      { x: 400, y: -20 },
      { x: 550, y: 0 },
      { x: 300, y: 200 },
      { x: 90, y: 100 },
      { x: 120, y: 20 },
      { x: 550, y: 90 },
      { x: 250, y: -20 },
      { x: 480, y: 180 },
    ].map((pos) => ({
      x: pos.x * (wrapperWidth / 230),
      y: pos.y * (wrapperHeight / 230),
    }));

    const mobilePositions = [
      { x: 120, y: -50 },
      { x: 0, y: -40 },
      { x: 70, y: -70 },
      { x: -140, y: 50 },
      { x: 70, y: 230 },
      { x: 120, y: 190 },
      { x: 10, y: 190 },
      { x: -140, y: 210 },
      { x: -100, y: 80 },
    ].map((pos) => ({
      x: pos.x * (wrapperWidth / 180),
      y: pos.y * (wrapperHeight / 180),
    }));

    return isMobile
      ? mobilePositions
      : desktopPositions.slice(0, ecosystemRoles.length);
  }, []);

  const dotPositions = useMemo(() => {
    const generateDotPositions = (count = 20) => {
      const positions = [];
      for (let i = 0; i < count; i++) {
        const left = Math.random() * 100;
        const top = Math.random() * 100;
        const scale = Math.random() * 0.5 + 0.5;
        positions.push({ left, top, scale });
      }
      return positions;
    };
    return generateDotPositions(20);
  }, []);

  useEffect(() => {
    if (!window.Lenis) {
      console.error(
        "Lenis is not loaded. Ensure the Lenis CDN script is included in your HTML."
      );
      return;
    }

    const lenis = new window.Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true,
      smoothTouch: true,
    });

    lenis.on("scroll", ScrollTrigger.update);

    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);

    return () => {
      lenis.destroy();
      gsap.ticker.remove(lenis.raf);
    };
  }, []);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      iconRefs.current.forEach((icon, index) => {
        if (icon) {
          gsap.fromTo(
            icon,
            { rotation: 0, y: 0, opacity: 0 },
            {
              rotation: 360,
              y: -30,
              opacity: 0.7,
              duration: 2,
              ease: "power1.inOut",
              repeat: -1,
              yoyo: true,
              delay: index * 0.2,
            }
          );
        }
      });

      if (whyChooseUsRef.current) {
        ScrollTrigger.create({
          trigger: whyChooseUsRef.current,
          start: "top top",
          end: `+=${window.innerHeight * 3.5}`,
          pin: true,
          scrub: true,
          pinSpacing: false,
        });

        gsap.fromTo(
          whyChooseUsRef.current.querySelector(`.${styles.whyChooseUsHeader}`),
          { opacity: 0, y: 100 },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: "power2.out",
            scrollTrigger: {
              trigger: whyChooseUsRef.current,
              start: "top 80%",
              end: "top 50%",
              scrub: true,
            },
          }
        );
      }

      if (aboutRef.current && nodeRefs.current.length > 0) {
        const wrapper = aboutRef.current.querySelector(
          `.${styles.ecosystemWrapper}`
        );
        if (wrapper) {
          const isMobile = window.innerWidth <= 768;
          const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;
          const wrapperWidth = isMobile ? 300 : isTablet ? 350 : 400;
          const wrapperHeight = wrapperWidth;

          nodeRefs.current.forEach((node, index) => {
            if (node && nodePositions[index]) {
              const endX = nodePositions[index].x;
              const endY = nodePositions[index].y;
              const nodeWidth =
                ecosystemRoles[index].type === "portrait" ? 70 : 90;
              const nodeHeight =
                ecosystemRoles[index].type === "portrait" ? 90 : 70;

              gsap.set(node, {
                x: endX - nodeWidth / 2,
                y: endY - nodeHeight / 2,
                opacity: 0,
                scale: 0,
              });

              gsap.to(node, {
                x: endX - nodeWidth / 2,
                y: endY - nodeHeight / 2,
                opacity: 1,
                scale: 1,
                duration: 0.7,
                ease: "power3.out",
                delay: index * 0.2,
                scrollTrigger: {
                  trigger: aboutRef.current,
                  start: "top 60%",
                  end: "bottom 20%",
                  toggleActions: "play none none none",
                },
              });

              gsap.to(node, {
                rotate: [5, -5, 3, -3, 0],
                duration: 0.5,
                repeat: 3,
                ease: "power1.inOut",
                delay: index * 0.2 + 0.5,
                scrollTrigger: {
                  trigger: aboutRef.current,
                  start: "top 60%",
                  end: "bottom 20%",
                  toggleActions: "play none none none",
                },
              });
            }
          });
        }
      }

      if (modulesRef.current && moduleCardRefs.current.length > 0) {
        moduleCardRefs.current.forEach((card, index) => {
          if (card) {
            const image = card.querySelector(`.${styles.moduleImage}`);
            const content = card.querySelector(`.${styles.moduleContent}`);
            const icon = card.querySelector(`.${styles.moduleIcon}`);

            gsap.fromTo(
              card,
              { opacity: 0, y: 100 },
              {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: "power3.out",
                scrollTrigger: {
                  trigger: card,
                  start: "top 90%",
                  end: "top 60%",
                  toggleActions: "play none none none",
                },
              }
            );

            if (image) {
              gsap.fromTo(
                image,
                { y: 0 },
                {
                  y: -50,
                  duration: 1,
                  ease: "linear",
                  scrollTrigger: {
                    trigger: card,
                    start: "top 90%",
                    end: "bottom 10%",
                    scrub: true,
                  },
                }
              );
            }

            if (content) {
              gsap.fromTo(
                content,
                { opacity: 0, x: index % 2 === 0 ? -100 : 100 },
                {
                  opacity: 1,
                  x: 0,
                  duration: 0.8,
                  ease: "power3.out",
                  scrollTrigger: {
                    trigger: card,
                    start: "top 90%",
                    end: "top 60%",
                    toggleActions: "play none none none",
                  },
                }
              );
            }

            if (icon) {
              gsap.fromTo(
                icon,
                { y: 0, opacity: 0.5 },
                {
                  y: -20,
                  opacity: 1,
                  duration: 1.5,
                  ease: "power1.inOut",
                  repeat: -1,
                  yoyo: true,
                  scrollTrigger: {
                    trigger: card,
                    start: "top 90%",
                    end: "bottom 10%",
                    toggleActions: "play none none none",
                  },
                }
              );
            }
          }
        });

        dotRefs.current.forEach((dot, index) => {
          if (dot) {
            gsap.fromTo(
              dot,
              { opacity: 0, scale: 0 },
              {
                opacity: 0.5,
                scale: dotPositions[index].scale,
                duration: 2,
                ease: "power1.inOut",
                repeat: -1,
                yoyo: true,
                delay: index * 0.2,
                scrollTrigger: {
                  trigger: modulesRef.current,
                  start: "top 90%",
                  end: "bottom 10%",
                  toggleActions: "play none none none",
                },
              }
            );
          }
        });
      }
    }, [modulesRef]);

    return () => ctx.revert();
  }, [nodePositions, dotPositions]);

  const imageVariants = {
    initial: { opacity: 1, scale: 1 },
    animate: (progress) => ({
      opacity: 1,
      scale: 1 + (progress / 100) * 0.3,
      transition: { duration: 6, ease: "linear" },
    }),
    exit: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
  };

  const testimonialSlideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeInOut" },
    },
    exit: (direction) => ({
      x: direction > 0 ? "-100%" : "100%",
      opacity: 0,
      transition: { duration: 0.5, ease: "easeInOut" },
    }),
  };

  useEffect(() => {
    let interval;
    if (loadingProgress < 100) {
      interval = setInterval(() => {
        setLoadingProgress((prev) => (prev >= 100 ? 0 : prev + 1));
      }, 30);
    } else {
      setTimeout(() => {
        setCurrentSlideChoose(
          (prev) => (prev + 1) % whyChooseUsFeatures.length
        );
        setLoadingProgress(0);
      }, 500);
    }
    return () => clearInterval(interval);
  }, [loadingProgress]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDirection(1);
      setCurrentSlide((prev) => (prev + 1) % contents.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setTestimonialDirection(1);
      setCurrentTestimonialSlide((prev) => (prev + 1) % testimonials.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const moduleIconPositions = useMemo(() => {
    const generateModuleIconPositions = (count = 10) => {
      const positions = [];
      for (let i = 0; i < count; i++) {
        const left = Math.round(Math.random() * 90 + 5);
        const top = Math.round(Math.random() * 90 + 5);
        const scale = parseFloat((Math.random() * 0.5 + 0.7).toFixed(2));
        let isFarEnough = true;
        for (const p of positions) {
          const dx = p.left - left;
          const dy = p.top - top;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 10) {
            isFarEnough = false;
            break;
          }
        }
        if (isFarEnough) {
          positions.push({ left, top, scale });
        } else {
          i--;
        }
      }
      return positions;
    };
    return generateModuleIconPositions(50);
  }, []);

  const moduleSportsIcons = useMemo(() => {
    return moduleIconPositions.map((pos, index) => {
      const randomIcon =
        baseIcons[Math.floor(Math.random() * baseIcons.length)];
      return { ...randomIcon, key: `module-${randomIcon.id}-${index}`, ...pos };
    });
  }, [moduleIconPositions]);

  const moduleIconRefs = useRef([]);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      moduleIconRefs.current.forEach((el, index) => {
        const icon = el?.querySelector(".gsap-icon");
        const container = modulesRef.current;
        if (!icon || !container) return;

        const sectionHeight = container.offsetHeight;
        const iconHeight = icon.offsetHeight;

        const tl = gsap.timeline({
          repeat: -1,
          delay: index * 0.3,
        });

        tl.fromTo(
          icon,
          {
            y: sectionHeight + iconHeight,
            opacity: 0,
          },
          {
            y: -iconHeight,
            opacity: 0.7,
            duration: 8,
            ease: "none",
          }
        );
      });
    }, modulesRef);

    return () => ctx.revert();
  }, []);

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [testimonialTouchStart, setTestimonialTouchStart] = useState(null);
  const [testimonialTouchEnd, setTestimonialTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      setDirection(1);
      setCurrentSlide((prev) => (prev + 1) % contents.length);
    }
    if (isRightSwipe) {
      setDirection(-1);
      setCurrentSlide((prev) => (prev === 0 ? contents.length - 1 : prev - 1));
    }
  };

  const onTestimonialTouchStart = (e) => {
    setTestimonialTouchEnd(null);
    setTestimonialTouchStart(e.targetTouches[0].clientX);
  };

  const onTestimonialTouchMove = (e) => {
    setTestimonialTouchEnd(e.targetTouches[0].clientX);
  };

  const onTestimonialTouchEnd = () => {
    if (!testimonialTouchStart || !testimonialTouchEnd) return;
    const distance = testimonialTouchStart - testimonialTouchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      setTestimonialDirection(1);
      setCurrentTestimonialSlide((prev) => (prev + 1) % testimonials.length);
    }
    if (isRightSwipe) {
      setTestimonialDirection(-1);
      setCurrentTestimonialSlide((prev) =>
        prev === 0 ? testimonials.length - 1 : prev - 1
      );
    }
  };

  // Navigation functions
  const handleGetStartedClick = () => {
    navigate("/reg-verification");
  };

  const handleJoinHubClick = () => {
    navigate("/reg-verification");
  };

  const handleLoginClick = () => {
    navigate("/login-arena");
  };

  const handleArrowClick = (direction) => {
    setLoadingProgress(0);
    setDirectionChoose(direction);
    setCurrentSlideChoose((prev) => {
      const newSlide = prev + direction;
      return newSlide < 0
        ? whyChooseUsFeatures.length - 1
        : newSlide % whyChooseUsFeatures.length;
    });
  };

  return (
    <div className={styles.landingPage}>
      <HeaderMain pageType="marketing" />
      <div className={styles.backgroundContainer}>
        <section
          ref={heroRef}
          className={styles.hero}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {sportsIcons.map(({ icon, key, left, top, scale }, index) => (
            <motion.div
              key={key}
              className={styles.sportsIcon}
              ref={(el) => (iconRefs.current[index] = el)}
              style={{ left: `${left}%`, top: `${top}%`, zIndex: 0 }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: isHeroInView ? 0.7 : 0,
                scale: isHeroInView ? scale : 0,
                y: [0, -15, 0],
              }}
              transition={{
                opacity: { duration: 0.6, delay: index * 0.15 },
                scale: {
                  duration: 0.6,
                  delay: index * 0.15,
                  type: "spring",
                  stiffness: 200,
                },
                y: {
                  duration: 3,
                  repeat: Infinity,
                  repeatType: "reverse",
                  delay: index * 0.2,
                },
              }}
              whileHover={{ scale: scale * 1.1 }}
            >
              {icon}
            </motion.div>
          ))}
          <div className={styles.contentWrapper}>
            <div className={styles.content}>
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={`content-${currentSlide}`}
                  variants={testimonialSlideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  custom={direction}
                >
                  <motion.h1>{contents[currentSlide]?.heading}</motion.h1>
                  <motion.p>{contents[currentSlide]?.description}</motion.p>
                </motion.div>
              </AnimatePresence>
              <motion.div className={styles.ctaButtons}>
                <motion.button
                  className={styles.ctaButton1}
                  whileHover={{ scale: 1.1, rotate: 0 }}
                  style={{ scale: 1, rotate: 4 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Join the Hub"
                  onClick={handleJoinHubClick}
                >
                  Join the Hub
                </motion.button>
                <motion.button
                  className={styles.ctaButton}
                  whileHover={{ scale: 1.1, rotate: 0 }}
                  style={{ scale: 1, rotate: -4 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Login"
                  onClick={handleLoginClick}
                >
                  Login
                </motion.button>
              </motion.div>
            </div>
            <div className={styles.image}>
              <AnimatePresence mode="wait" custom={direction}>
                <motion.img
                  key={`image-${currentSlide}`}
                  src={contents[currentSlide]?.image}
                  alt={contents[currentSlide]?.heading}
                  variants={testimonialSlideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  custom={direction}
                />
              </AnimatePresence>
            </div>
          </div>
          <div className={styles.sliderDots}>
            {contents.map((_, index) => (
              <div
                key={`dot-${index}`}
                className={`${styles.dot} ${
                  index === currentSlide ? styles.active : ""
                }`}
                onClick={() => {
                  setDirection(index > currentSlide ? 1 : -1);
                  setCurrentSlide(index);
                }}
              />
            ))}
          </div>
        </section>
      </div>
      <motion.h2
        className={styles.modulesHeader}
        initial={{ opacity: 1, scale: 0.8, y: 50 }}
        whileInView={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: false, margin: "0px 0px -200px 0px" }}
      >
        About Us
      </motion.h2>

      <section ref={whyChooseUsRef} className={styles.about}>
        <div className={styles.aboutContainer}>
          <div className={styles.ecosystemWrapper}>
            <motion.div
              initial={{ opacity: 0, scale: 1, y: 0 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              viewport={{ once: false, margin: "0px 0px -200px 0px" }}
              className={styles.aboutText}
              style={{ textAlign: "center", position: "relative", zIndex: 10 }}
            >
              <h2 className={styles.aboutHeader}>
                We Don’t Just Do Sports. We Connect It All.
              </h2>
              <p>
                Dsportshub is the digital heartbeat of the sports world. From
                players to brands, AI to analytics — we unify every part of the
                ecosystem through innovation and collaboration. Built for speed.
                Designed for unity. Powered by intelligence.
              </p>
            </motion.div>
            {ecosystemRoles.map(({ image, type }, index) => {
              const isPortrait = type === "portrait";
              const mobileWidth = isPortrait ? "60px" : "90px";
              const mobileHeight = isPortrait ? "80px" : "60px";
              const desktopWidth = isPortrait ? "60px" : "110px";
              const desktopHeight = isPortrait ? "80px" : "90px";

              return (
                <motion.div
                  key={`node-${index}`}
                  className={styles.ecosystemNode}
                  ref={(el) => (nodeRefs.current[index] = el)}
                  style={{
                    width: isMobileView ? mobileWidth : desktopWidth,
                    height: isMobileView ? mobileHeight : desktopHeight,
                    position: "absolute",
                    left: `${nodePositions[index].x}px`,
                    top: `${nodePositions[index].y}px`,
                    zIndex: 2,
                  }}
                  aria-label={`Ecosystem role: ${type}`}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: false, amount: 0.1 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                >
                  <img
                    src={image}
                    alt={`Ecosystem role ${index}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "10px",
                    }}
                  />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section ref={aboutRef} className={styles.whyChooseUs}>
        <motion.h2
          className={`${styles.modulesHeader} ${styles.whyChooseUsMain}`}
          initial={{ opacity: 1, scale: 0.8, y: 50 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: false, margin: "0px 0px -200px 0px" }}
        >
          Why Choose Us
        </motion.h2>
        <motion.h2
          initial={{ opacity: 0, scale: 1, y: 0 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 2, ease: "easeOut" }}
          viewport={{ once: false, margin: "0px 0px -200px 0px" }}
          className={styles.whyChooseUsHeader}
        >
          Every Angle.
          <br />
          Every Player. Every Play.
        </motion.h2>
        <div className={styles.arrowWrapper}>
          <button
            onClick={() => handleArrowClick(-1)}
            className={styles.arrowButton}
          >
            ←
          </button>
          <button
            onClick={() => handleArrowClick(1)}
            className={styles.arrowButton}
          >
            →
          </button>
        </div>
        <motion.div className={styles.featureGrid}>
          <AnimatePresence mode="wait" custom={directionChoose}>
            <motion.div
              key={currentSlideChoose}
              className={styles.contentWrapper2}
              custom={directionChoose}
              variants={testimonialSlideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.5 }}
              onAnimationComplete={() => setLoadingProgress(0)}
            >
              <div className={styles.content2}>
                <h1>{whyChooseUsFeatures[currentSlideChoose]?.heading}</h1>
                <div>
                  <p>{whyChooseUsFeatures[currentSlideChoose]?.description}</p>
                  <div className={styles.progressBar}>
                    <motion.div
                      className={styles.progressFill}
                      style={{ width: `${loadingProgress}%` }}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.image2}>
                <AnimatePresence mode="wait">
                  <motion.img
                    key={`whyChooseUs-image-${currentSlideChoose}`}
                    src={whyChooseUsFeatures[currentSlideChoose]?.image}
                    alt={whyChooseUsFeatures[currentSlideChoose]?.heading}
                    variants={imageVariants}
                    initial="initial"
                    animate={{ ...imageVariants.animate(loadingProgress) }}
                    exit="exit"
                    custom={loadingProgress}
                  />
                </AnimatePresence>
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </section>

      <section ref={modulesRef} className={styles.modules}>
        {moduleSportsIcons.map(({ icon, key, left, top, scale }, index) => (
          <motion.div
            key={key}
            className={styles.moduleSportsIcon}
            ref={(el) => (moduleIconRefs.current[index] = el)}
            style={{ left: `${left}%`, top: `${top}%`, zIndex: 0 }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: 0.7,
              scale: scale,
            }}
            transition={{
              opacity: { duration: 0.6, delay: index * 0.15 },
              scale: {
                duration: 0.6,
                delay: index * 0.15,
                type: "spring",
                stiffness: 200,
              },
            }}
            whileHover={{ scale: scale * 1.1 }}
          >
            <div className="gsap-icon">{icon}</div>
          </motion.div>
        ))}
        <motion.h2
          className={styles.modulesHeader}
          initial={{ opacity: 1, scale: 0.8, y: 50 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: false, margin: "0px 0px -200px 0px" }}
        >
          Our Modules
        </motion.h2>
        <div className={styles.modulesWrapper}>
          {modules.map((module, index) => (
            <motion.div
              key={`module-${index}`}
              className={`${styles.moduleCard} ${
                index % 2 === 0 ? styles.leftImage : styles.rightImage
              }`}
              initial={{ opacity: 0, scale: 1, y: 0 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 2, ease: "easeOut" }}
              viewport={{ once: true, margin: "0px 0px -200px 0px" }}
            >
              <div className={styles.moduleImage}>
                <img src={module.image} alt={module.title} />
              </div>
              <div className={styles.moduleContent}>
                <img
                  src={module.icon}
                  alt={`${module.title} icon`}
                  className={styles.moduleIcon}
                />
                <div className={styles.moduleText}>
                  <h3>{module.title}</h3>
                  <p>{module.description}</p>
                  <motion.button
                    className={styles.moduleCta}
                    aria-label={module.cta}
                  >
                    {module.cta}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
      <div className={styles.TestimonialsHeaderbg}>
        <motion.h2
          className={styles.TestimonialsHeader}
          initial={{ opacity: 1, scale: 0.8, y: 50 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: false, margin: "0px 0px -200px 0px" }}
        >
          Don't take our word for it!
          <br />
          Hear it from our partners
        </motion.h2>
      </div>
      <section
        ref={testimonialsRef}
        className={styles.testimonials}
        onTouchStart={onTestimonialTouchStart}
        onTouchMove={onTestimonialTouchMove}
        onTouchEnd={onTestimonialTouchEnd}
      >
        <div className={styles.testimonialContentWrapper}>
          <div className={styles.testimonialContent}>
            <AnimatePresence mode="wait" custom={testimonialDirection}>
              <motion.div
                key={`testimonial-content-${currentTestimonialSlide}`}
                variants={testimonialSlideVariants}
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                custom={testimonialDirection}
              >
                <motion.p className={styles.testimonialQuote}>
                  {testimonials[currentTestimonialSlide]?.quote}
                </motion.p>
                <motion.h3 className={styles.testimonialName}>
                  {testimonials[currentTestimonialSlide]?.name}
                </motion.h3>
                <motion.p className={styles.testimonialRole}>
                  {testimonials[currentTestimonialSlide]?.role}
                </motion.p>
              </motion.div>
            </AnimatePresence>
          </div>
          <div className={styles.testimonialImage}>
            <AnimatePresence mode="wait" custom={testimonialDirection}>
              <motion.img
                key={`testimonial-image-${currentTestimonialSlide}`}
                src={testimonials[currentTestimonialSlide]?.image}
                alt={testimonials[currentTestimonialSlide]?.name}
                variants={testimonialSlideVariants}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                custom={testimonialDirection}
              />
            </AnimatePresence>
          </div>
        </div>
        <div className={styles.testimonialSliderDots}>
          {testimonials.map((_, index) => (
            <div
              key={`testimonial-dot-${index}`}
              className={`${styles.dot} ${
                index === currentTestimonialSlide ? styles.active : ""
              }`}
              ref={(el) => (testimonialDotRefs.current[index] = el)}
              onClick={() => {
                setTestimonialDirection(
                  index > currentTestimonialSlide ? 1 : -1
                );
                setCurrentTestimonialSlide(index);
              }}
            />
          ))}
        </div>
      </section>
      <section ref={ctaRef} className={styles.ctaSection}>
        <div className={styles.ctaContainer}>
          <h2 className={styles.ctaHeader}>Join the Future of Sports</h2>
          <p className={styles.ctaText}>
            Be part of the Dsportshub community. Connect, compete, and conquer
            with the ultimate sports platform.
          </p>
          <motion.div className={styles.ctaButtons}>
            <motion.button
              className={styles.ctaButton1}
              whileHover={{ scale: 1.1, rotate: 0 }}
              style={{ scale: 1, rotate: 4 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Get Started Now"
              onClick={handleGetStartedClick}
            >
              Get Started Now
            </motion.button>
            <motion.button
              className={styles.ctaButton}
              whileHover={{ scale: 1.1, rotate: 0 }}
              style={{ scale: 1, rotate: -4 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Learn More"
            >
              Learn More
            </motion.button>
          </motion.div>
        </div>
      </section>
      <footer ref={footerRef} className={styles.footer}>
        <div className={styles.footerContainer}>
          <div className={styles.footerLogo}>
            <img src="/logo.png" alt="Dsportshub Logo" />
          </div>
          <div className={styles.footerLinks}>
            <div className={styles.footerColumn}>
              <h4>Company</h4>
              <a href="#about">About Us</a>
              <a href="#careers">Careers</a>
              <a href="#contact">Contact</a>
            </div>
            <div className={styles.footerColumn}>
              <h4>Resources</h4>
              <a href="#blog">Blog</a>
              <a href="#help">Help Center</a>
              <a href="#privacy">Privacy Policy</a>
            </div>
            <div className={styles.footerColumn}>
              <h4>Connect</h4>
              <a href="#twitter">Twitter</a>
              <a href="#linkedin">LinkedIn</a>
              <a href="#instagram">Instagram</a>
            </div>
          </div>
          <div className={styles.footerBottom}>
            <p>
              &copy; {new Date().getFullYear()} Dsportshub. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketingPage;
